/**
 * Multi-select contact picker for the Presets admin tab.
 *
 * Wraps `useContactSearch` (POST /pb/admin/search) and renders selected
 * contacts as removable chips plus a debounced search results list. Two
 * modes:
 *
 *   - mode="prefixed"  -> emits "ContactPersonal/<short>" strings (used by
 *                         individual-contact picker; matches the API
 *                         contract for definition.individual_contact_ids).
 *   - mode="numeric"   -> emits numeric edumate contact ids (used by the
 *                         manager picker; manager_contact_ids on the API
 *                         is `int[]`).
 *
 * Initial-label seeding lets edit-mode chips render real names immediately
 * for pre-saved selections without forcing the user to re-search each one.
 * Missing labels fall back to a generic placeholder rather than the raw id
 * (admin-friendly).
 */
import { useEffect, useMemo, useState } from "react";
import {
  IonCheckbox,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonSearchbar,
  IonSpinner,
} from "@ionic/react";
import { chevronDownOutline, closeOutline, personOutline } from "ionicons/icons";
import ContactProfileImage from "../ContactProfileImage";
import { useContactSearch } from "../../hooks/useContactSearch";

import "./presets.css";

interface ContactSearchRow {
  _id?: string;
  // Numeric Edumate ContactId from the search. Required for numeric-mode
  // selection (owner / manager) since those emit int ids; ``_id`` is the
  // ContactPersonal hex id and becomes NaN if coerced to a number.
  EdumateContactId?: number;
  Name?: string;
  Staff?: boolean;
  Student?: boolean;
  Carer?: boolean;
  [key: string]: unknown;
}

/** Styling/behaviour shared by both modes. */
interface CommonPickerProps {
  placeholder?: string;
  /** Contact types to search across. Defaults to staff+student+carer for
   *  individual pickers and staff-only for manager pickers. */
  types?: string[];
  /** Chip fallback when no label is known yet. */
  unknownLabel?: string;
  /** Opt-in compact variant (Create-preset redesign): searchbar + trailing
   *  chevron, chips below, an "N <noun> selected" count, and a results panel
   *  that stays collapsed until focused/typed/toggled. Defaults to the legacy
   *  always-open layout so other consumers (EditPresetModal) are unaffected. */
  collapsible?: boolean;
  /** Singular noun for the collapsible count line, e.g. "contact" ->
   *  "2 contacts selected". Omit to suppress the count (single-select owner). */
  countNoun?: string;
}

interface PrefixedPickerProps extends CommonPickerProps {
  mode: "prefixed";
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  initialLabels?: Record<string, string>;
  /** Render each selected chip with the contact's profile image (gm#127).
   *  Prefixed mode only: chip ids are "ContactPersonal/<short>" and the blob
   *  image is keyed by that short id. Numeric chips carry Edumate ids, which
   *  have no blob mapping, so the prop deliberately doesn't exist there. */
  showAvatars?: boolean;
}

interface NumericPickerProps extends CommonPickerProps {
  mode: "numeric";
  selectedIds: number[];
  onChange: (ids: number[]) => void;
  initialLabels?: Record<number, string>;
}

type ContactPickerProps = PrefixedPickerProps | NumericPickerProps;

export function ContactPicker(props: ContactPickerProps) {
  const {
    mode,
    initialLabels,
    placeholder = "Search to add contacts",
    types = ["staff", "student", "carer"],
    unknownLabel = "Contact",
    collapsible = false,
    countNoun,
  } = props;
  // Collapsible variant: results panel is hidden until the user focuses the
  // searchbar, types, or toggles the chevron. Typing always reveals it.
  const [resultsOpen, setResultsOpen] = useState(false);
  const {
    contactSearchResult,
    contactSearchResultLoaded,
    setContactSearchFilter,
  } = useContactSearch();
  const [search, setSearch] = useState("");
  const [submittedSearch, setSubmittedSearch] = useState("");
  // Use a stringly-keyed label store internally to keep one code path; pickers
  // expose typed inputs but we normalise both flavours to keys here. Numeric
  // ids stringify trivially; round-tripping is lossless.
  // Normalise label keys to the same shape chipLabel() looks them up by.
  // chipLabel() strips the "ContactPersonal/" prefix before lookup in
  // prefixed mode, so prefixed keys MUST be stored unprefixed - otherwise
  // edit-mode chips render the "Contact" placeholder even when the API
  // supplied a name. Codex round 1 Low.
  const normaliseLabelKey = (key: string): string =>
    mode === "prefixed" && key.includes("/") ? key.split("/", 2)[1] : key;

  const [labelStore, setLabelStore] = useState<Record<string, string>>(() => {
    if (!initialLabels) return {};
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(initialLabels)) {
      out[normaliseLabelKey(String(k))] = String(v);
    }
    return out;
  });

  // Merge any new initialLabels (e.g. a parent re-seed after detail refetch)
  // WITHOUT overwriting in-session discoveries.
  useEffect(() => {
    if (!initialLabels) return;
    setLabelStore((prev) => {
      let mutated = false;
      const next = { ...prev };
      for (const [k, v] of Object.entries(initialLabels)) {
        const key = normaliseLabelKey(String(k));
        if (v && next[key] === undefined) {
          next[key] = String(v);
          mutated = true;
        }
      }
      return mutated ? next : prev;
    });
    // normaliseLabelKey closes over `mode`; effect re-runs if mode flips.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialLabels, mode]);

  // Debounce search input -> filter setter.
  useEffect(() => {
    const trimmed = search.trim();
    if (!trimmed) {
      setSubmittedSearch("");
      return;
    }
    const handle = setTimeout(() => {
      setContactSearchFilter({
        search: trimmed,
        active_only: 1,
        type: types,
        limit: 30,
        page: 1,
      });
      setSubmittedSearch(trimmed);
    }, 300);
    return () => clearTimeout(handle);
    // useContactSearch's setter is stable enough; including it triggers re-runs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, types.join(",")]);

  // The label-store key a row uses, matching what chipLabel() looks up:
  //   prefixed -> the ContactPersonal hex _id
  //   numeric  -> the stringified Edumate ContactId (the emitted value)
  // Numeric rows without an Edumate id have no stable label key and are not
  // selectable, so they contribute nothing.
  const labelKeyForRow = (row: ContactSearchRow): string | undefined => {
    if (mode === "prefixed") return row._id;
    return row.EdumateContactId != null
      ? String(row.EdumateContactId)
      : undefined;
  };

  // Stash any new contact -> name pairs we see in search results so chips
  // for those ids render real names even after the search clears.
  useEffect(() => {
    if (!Array.isArray(contactSearchResult)) return;
    setLabelStore((prev) => {
      const next = { ...prev };
      let mutated = false;
      for (const row of contactSearchResult as ContactSearchRow[]) {
        const key = labelKeyForRow(row);
        if (key && row.Name && next[key] !== row.Name) {
          next[key] = row.Name;
          mutated = true;
        }
      }
      return mutated ? next : prev;
    });
    // labelKeyForRow closes over `mode`; re-run if the mode flips.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactSearchResult, mode]);

  // Build the selected-set lookup using mode-aware keys.
  const selectedSet = useMemo(() => {
    if (mode === "prefixed") {
      return new Set(props.selectedIds);
    }
    return new Set(props.selectedIds.map(String));
  }, [mode, props.selectedIds]);

  // A row is selectable when it carries the id this picker emits:
  //   prefixed -> any row with an _id
  //   numeric  -> only rows with a numeric Edumate ContactId (owner /
  //               manager ids are int on the API; the hex _id is NaN if
  //               coerced and would silently drop the selection).
  const isRowSelectable = (row: ContactSearchRow): boolean =>
    mode === "prefixed" ? row._id != null : row.EdumateContactId != null;

  const isRowSelected = (row: ContactSearchRow): boolean => {
    if (mode === "prefixed") {
      return row._id != null && selectedSet.has(`ContactPersonal/${row._id}`);
    }
    return (
      row.EdumateContactId != null &&
      selectedSet.has(String(row.EdumateContactId))
    );
  };

  const toggleRow = (row: ContactSearchRow) => {
    const label = row.Name ?? "";
    if (mode === "prefixed") {
      if (row._id == null) return;
      const key = `ContactPersonal/${row._id}`;
      if (props.selectedIds.includes(key)) {
        props.onChange(props.selectedIds.filter((id) => id !== key));
      } else {
        props.onChange([...props.selectedIds, key]);
        if (label) {
          setLabelStore((prev) => ({ ...prev, [row._id as string]: label }));
        }
      }
    } else {
      const numeric = row.EdumateContactId;
      if (numeric == null) return;
      if (props.selectedIds.includes(numeric)) {
        props.onChange(props.selectedIds.filter((id) => id !== numeric));
      } else {
        props.onChange([...props.selectedIds, numeric]);
        if (label) {
          setLabelStore((prev) => ({ ...prev, [String(numeric)]: label }));
        }
      }
    }
  };

  const removeChip = (anyId: string | number) => {
    if (mode === "prefixed") {
      const key = String(anyId);
      props.onChange(props.selectedIds.filter((id) => id !== key));
    } else {
      const numeric = Number(anyId);
      props.onChange(props.selectedIds.filter((id) => id !== numeric));
    }
  };

  // Resolve a chip's display label. For prefixed mode the chip id is
  // "ContactPersonal/<short>"; we look up by the short part.
  const chipLabel = (anyId: string | number): string => {
    let lookupKey: string;
    if (mode === "prefixed") {
      const str = String(anyId);
      lookupKey = str.includes("/") ? str.split("/", 2)[1] : str;
    } else {
      lookupKey = String(anyId);
    }
    return labelStore[lookupKey] ?? unknownLabel;
  };

  // Avatars are prefixed-mode-only (see PrefixedPickerProps.showAvatars);
  // the blob image id is the chip id's short part, same as chipLabel's key.
  // Narrow via props.mode - the destructured `mode` doesn't narrow `props`.
  const showAvatars = props.mode === "prefixed" && props.showAvatars === true;
  const chipImageId = (anyId: string | number): string => {
    const str = String(anyId);
    return str.includes("/") ? str.split("/", 2)[1] : str;
  };

  // Render-side helpers: stale = user typed but debounce not fired yet OR
  // request in flight.
  const isStale = search.trim() !== "" && search.trim() !== submittedSearch;
  const isLoading =
    search.trim() !== "" && (isStale || !contactSearchResultLoaded);

  // Selected ids list normalised to a (key, displayId) pair for chip rendering.
  const chipEntries: Array<{ chipKey: string; displayId: string | number }> =
    useMemo(() => {
      if (mode === "prefixed") {
        return props.selectedIds.map((id) => ({ chipKey: id, displayId: id }));
      }
      return props.selectedIds.map((id) => ({
        chipKey: String(id),
        displayId: id,
      }));
    }, [mode, props.selectedIds]);

  // Collapsible variant hides the results until the user engages. resultsOpen
  // is the single source of truth so the chevron can always collapse the list;
  // starting a search auto-reveals it (see the searchbar handler). The legacy
  // layout keeps the panel permanently open.
  const showResults = !collapsible || resultsOpen;

  const chipsBlock = chipEntries.length > 0 && (
    <div
      className="preset-picker-chips"
      aria-label={`${chipEntries.length} selected`}
    >
      {chipEntries.map(({ chipKey, displayId }) => (
        <span
          key={chipKey}
          className={
            showAvatars
              ? "preset-picker-chip preset-picker-chip--person"
              : "preset-picker-chip"
          }
        >
          {showAvatars && (
            <span className="preset-chip-avatar" aria-hidden="true">
              <ContactProfileImage
                profile={{ contactId: chipImageId(displayId) }}
              />
            </span>
          )}
          {chipLabel(displayId)}
          <button
            type="button"
            className="preset-picker-chip-remove"
            aria-label={`Remove ${chipLabel(displayId)}`}
            onClick={() => removeChip(displayId)}
          >
            <IonIcon icon={closeOutline} />
          </button>
        </span>
      ))}
    </div>
  );

  const resultsBlock = (
    <div className="preset-picker-results">
      {!search.trim() ? (
        <div className="preset-picker-empty">
          <IonIcon icon={personOutline} aria-hidden="true" />
          <p>Type a name to search</p>
        </div>
      ) : isLoading ? (
        <div className="preset-picker-empty">
          <IonSpinner name="dots" />
          <p>Searching...</p>
        </div>
      ) : !Array.isArray(contactSearchResult) ||
        contactSearchResult.length === 0 ? (
        <div className="preset-picker-empty">
          <IonIcon icon={personOutline} aria-hidden="true" />
          <p>No matching contacts</p>
        </div>
      ) : (
        <IonList lines="inset">
          {(contactSearchResult as ContactSearchRow[]).map((row) => {
            if (!row._id) return null;
            const checked = isRowSelected(row);
            const selectable = isRowSelectable(row);
            return (
              <IonItem
                key={row._id}
                button
                disabled={!selectable}
                onClick={() => toggleRow(row)}
                className="preset-picker-result-item"
              >
                <div slot="start" aria-hidden="true">
                  <ContactProfileImage profile={{ contactId: row._id }} />
                </div>
                <IonLabel>
                  <h3>{row.Name ?? "(unnamed)"}</h3>
                  <p>
                    {row.Staff ? "Staff" : ""}
                    {row.Student ? "Student" : ""}
                    {row.Carer ? "Carer" : ""}
                    {!selectable ? " (no contact id)" : ""}
                  </p>
                </IonLabel>
                <IonCheckbox
                  slot="end"
                  checked={checked}
                  disabled={!selectable}
                  aria-label={`Select ${row.Name ?? "contact"}`}
                  onClick={(e) => e.stopPropagation()}
                  onIonChange={(e) => {
                    if (e.detail.checked !== checked) {
                      toggleRow(row);
                    }
                  }}
                />
              </IonItem>
            );
          })}
        </IonList>
      )}
    </div>
  );

  if (!collapsible) {
    return (
      <div className="preset-picker">
        <IonSearchbar
          className="preset-picker-searchbar"
          value={search}
          onIonInput={(e) => setSearch(e.detail.value ?? "")}
          placeholder={placeholder}
          debounce={0}
          showClearButton="focus"
        />
        {chipsBlock}
        {resultsBlock}
      </div>
    );
  }

  return (
    <div className="preset-picker preset-picker--collapsible">
      <div className="preset-picker-searchrow">
        <IonSearchbar
          className="preset-picker-searchbar"
          value={search}
          onIonInput={(e) => {
            const value = e.detail.value ?? "";
            if (search.trim() === "" && value.trim() !== "")
              setResultsOpen(true);
            setSearch(value);
          }}
          onIonFocus={() => setResultsOpen(true)}
          placeholder={placeholder}
          debounce={0}
          showClearButton="focus"
        />
        <button
          type="button"
          className="preset-picker-toggle"
          aria-expanded={showResults}
          aria-label={showResults ? "Hide results" : "Show results"}
          onClick={() => setResultsOpen((o) => !o)}
        >
          <IonIcon
            icon={chevronDownOutline}
            className={showResults ? "preset-picker-toggle-icon open" : "preset-picker-toggle-icon"}
          />
        </button>
      </div>
      {chipsBlock}
      {countNoun && chipEntries.length > 0 && (
        <div className="preset-picker-count">
          {chipEntries.length} {countNoun}
          {chipEntries.length === 1 ? "" : "s"} selected
        </div>
      )}
      {showResults && resultsBlock}
    </div>
  );
}
